import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography
} from '@customink/pigment-react';
import {CardActionArea} from '@customink/pigment-react/lib/components/Card';
import {useMobileBreakpoint} from '@customink/pigment-react/lib/utils/useBreakpoint';
import React from 'react';
import ProductCardPriceDetails from '../../features/products/components/ProductCard/components/ProductCardPriceDetails/ProductCardPriceDetails';
import ProductCardRating from '../../features/products/components/ProductCard/components/ProductCardRating';
import ProductCardSizesInfo from '../../features/products/components/ProductCard/components/ProductCardSizesInfo';
import {
  checkIsUnitPriceDisplayTestApplicable,
  UNITS_QTY
} from '../../utils/unitPriceDisplayTest';
import searchParams from '../../utils/url';

const ProductDetailsCard = ({
  cardContentSx = {},
  defaultQuoteQty,
  defaultUnitPrices,
  generalComments,
  imageSrc,
  imageSrcSet,
  linkHref,
  minQty,
  name,
  onClick,
  primaryCategoryId,
  productId,
  ratingCount,
  ratingScore,
  sizes = [],
  styleType,
  sx = {}
}) => {
  const hasSizes = sizes.length > 1;
  const isMobile = useMobileBreakpoint();
  const pricingQuantity = checkIsUnitPriceDisplayTestApplicable({
    categoryId: primaryCategoryId,
    defaultUnitPrices,
    desiredQty: UNITS_QTY,
    styleType
  })
    ? UNITS_QTY
    : defaultQuoteQty;
  const useNoMinimum =
    searchParams().get('quote_qty') === 'min' &&
    minQty === 1 &&
    defaultUnitPrices.find((price) => price.quantity === 1);

  return (
    <Card sx={{display: 'flex', height: '100%', ...sx}}>
      <CardActionArea
        onClick={onClick}
        aria-label={name}
        aria-hidden
        disableRipple
        tabIndex={-1}
        sx={{
          '& .MuiCardActionArea-focusHighlight': {display: 'none'},
          display: 'flex',
          flexDirection: 'column'
        }}
        {...(linkHref ? {href: linkHref} : {})}>
        <Box
          sx={{
            '&:before': {
              boxShadow: '0px 0px 60px 24px #f5f5f5 inset',
              content: '""',
              inset: 0,
              mixBlendMode: 'darken',
              position: 'absolute',
              zIndex: 2
            },
            height: {md: 'unset !important', sm: '9.5rem'},
            objectFit: 'cover',
            overflow: 'hidden',
            paddingTop: {md: '100%', sm: '6.5rem'},
            position: 'relative',
            width: {md: '100%', sm: '10.1rem'}
          }}>
          <Box
            component="img"
            sx={{
              height: '100%',
              left: '50%',
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            src={imageSrc}
            srcSet={imageSrcSet}
            alt={name}
          />
        </Box>
        <CardContent
          sx={{
            display: 'flex',
            flex: '1',
            flexDirection: 'column',
            gap: '0.3125rem',
            height: '100%',
            justifyContent: {md: 'flex-start', sm: 'center'},
            mt: 0,
            padding: {md: '0.8125rem 1rem', sm: '0.5rem'},
            width: '100%',
            ...cardContentSx
          }}>
          <Typography
            sx={{
              color: '#242424',
              display: '-webkit-box',
              fontSize: {md: '0.875rem', sm: '0.8125rem'},
              fontWeight: 700,
              height: {md: '2.2rem !important', sm: '2rem !important'},
              letterSpacing: 0,
              lineHeight: '125%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2
            }}>
            {name}
          </Typography>
          {((ratingScore && ratingCount) || hasSizes) && (
            <Stack
              direction="row"
              gap="0.5rem"
              alignItems="center"
              height="1.25rem">
              {ratingScore && ratingCount && (
                <ProductCardRating
                  count={Number(ratingCount.replace(/[()]/g, '').split(' ')[0])}
                  score={Number(ratingScore)}
                  generalComments={generalComments}
                  name={name}
                />
              )}
              {ratingScore && ratingCount && hasSizes && (
                <Divider orientation="vertical" flexItem />
              )}
              {hasSizes && (
                <ProductCardSizesInfo productId={productId} sizes={sizes} />
              )}
            </Stack>
          )}
          <ProductCardPriceDetails
            defaultUnitPrices={defaultUnitPrices}
            productId={productId}
            quantity={useNoMinimum ? 1 : pricingQuantity}
            minQty={minQty}
            name={name}
            frontColor={1}
            backColor={0}
            isMobile={isMobile}
            hasSinglesEnabledColors={false}
            hasDirtyQuoteQuantity={false}
            specialty={false}
            productColors={[]}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              lineHeight: '1.25',
              mt: '0.0625rem',
              width: '100%'
            }}>
            <Typography sx={{color: '#4A4A4A', fontSize: '0.8125rem'}}>
              {minQty === 1 ? 'No Minimum' : `Minimum ${minQty}`}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProductDetailsCard;
